import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface DemoItem {
  id: string;
  title: string;
  description: string;
  content: string;
  type: 'iframe' | 'component';
  translation_urls?: {
    translation_1: string;
    translation_2: string;
  };
  comingSoon?: boolean;
}

const DEMO_ITEMS: DemoItem[] = [
  {
    id: 'qian-bot',
    title: 'QianBot',
    description: 'Interact with our advanced conversational AI system that understands context, intent, and natural language patterns.',
    content: 'https://qianbot.dhtsolution.com',
    type: 'iframe',
    comingSoon: true
  },
  {
    id: 'docgraph-explorer',
    title: 'DocGraph Explorer',
    description: 'From Raw Text to Connected Knowledge.',
    content: 'https://192.168.157.123:8502/?embed=true',
    type: 'iframe',
    comingSoon: true
  },
  {
    id: 'km',
    title: 'Knowledge Management System',
    description: '',
    content: 'https://km.dhtsolution.com', 
    type: 'iframe',
    comingSoon: true
  },
  {
    id: 'translation',
    title: 'Translation',
    description: 'Two-way multilingual translation ',
    content: 'https://192.168.157.161:3080/',
    type: 'iframe',
    translation_urls: {
      translation_1: 'https://192.168.157.161:3080/',
      translation_2: 'https://192.168.157.161:3081'
    },
    comingSoon: true
  },
  // {
  //   id: 'nlp-engine',
  //   title: 'NLP Engine',
  //   description: 'Experience our natural language processing capabilities for text analysis and understanding.',
  //   content: 'https://example.com/nlp-demo',
  //   type: 'iframe',
  //   comingSoon: true
  // }
];

interface DemoProps {
  language: string;
}

const Demo: React.FC<DemoProps> = ({ language }) => {
  const [selectedDemo, setSelectedDemo] = useState<DemoItem | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [micPermission, setMicPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');

  useEffect(() => {
    // Set initial demo
    if (DEMO_ITEMS.length > 0) {
      setSelectedDemo(DEMO_ITEMS[0]);
    }

    // Check if mobile on initial load
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleDemoSelect = (demo: DemoItem) => {
    setSelectedDemo(demo);
    if (isMobile) {
      setMenuOpen(false);
    }
  };

  const requestMicrophoneAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMicPermission('granted');
      // Keep the stream reference if needed, or close it right away if just checking permissions
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      setMicPermission('denied');
      console.error('Microphone access denied:', err);
    }
  };

  const renderDemoContent = () => {
    if (!selectedDemo) return null;

    if (selectedDemo.comingSoon) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <div className="text-4xl font-bold text-blue-700 mb-4">{language === 'zh' ? '即將上線' : 'Coming Soon'}</div>
          <div className="text-lg text-gray-600">{language === 'zh' ? '此演示系統尚未開放，敬請期待！' : 'This demo system is not yet available. Stay tuned!'}</div>
        </div>
      );
    }

    if (selectedDemo.id === 'translation' && selectedDemo.translation_urls) {
      return (
        <div className="h-full w-full flex flex-col">
          <div className="flex justify-end mb-2">
            <button
              onClick={requestMicrophoneAccess}
              className={`px-4 py-2 rounded-md flex items-center ${
                micPermission === 'granted' 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : micPermission === 'denied'
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-blue-700 hover:bg-blue-800'
              } text-white`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
              </svg>
              {micPermission === 'granted' 
                ? 'Microphone Access Granted' 
                : micPermission === 'denied' 
                  ? 'Microphone Access Denied' 
                  : 'Request Microphone Access'}
            </button>
          </div>
          <div className="flex flex-col md:flex-row h-full gap-2">
            <div className="h-1/2 md:h-full md:w-1/2 rounded-lg overflow-hidden border border-gray-200 shadow-lg">
              <iframe 
                src={selectedDemo.translation_urls.translation_1} 
                title="Translation 1" 
                className="w-full h-full"
                allow="microphone; camera"
                allowFullScreen
              ></iframe>
            </div>
            <div className="h-1/2 md:h-full md:w-1/2 rounded-lg overflow-hidden border border-gray-200 shadow-lg">
              <iframe 
                src={selectedDemo.translation_urls.translation_2} 
                title="Translation 2" 
                className="w-full h-full"
                allow="microphone; camera"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      );
    } else if (selectedDemo.type === 'iframe') {
      return (
        <div className="h-full w-full rounded-lg overflow-hidden border border-gray-200 shadow-lg">
          <iframe 
            src={selectedDemo.content} 
            title={selectedDemo.title} 
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      );
    } else {
      // For non-iframe content (like QR codes, text, etc.)
      return (
        <div className="h-full w-full flex flex-col items-center justify-center p-8 rounded-lg border border-gray-200 shadow-lg bg-white">
          <div className="p-4 bg-white rounded-lg border-2 border-blue-500 shadow-sm mb-6">
            <img 
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selectedDemo.content)}`} 
              alt={`QR Code for ${selectedDemo.title}`} 
              className="w-[200px] h-[200px]"
            />
          </div>
          <p className="font-medium text-lg text-gray-700 text-center">{selectedDemo.content}</p>
        </div>
      );
    }
  };

  return (
    <div className="h-screen bg-blue-50 flex flex-col">
      {/* Navigation header */}
      <header className="bg-blue-900 text-white p-4 flex justify-between items-center shadow-md">
        <Link to="/" className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          <span className="font-bold text-lg">{language === 'zh' ? '返回主頁' : 'Back to Home'}</span>
        </Link>
        
        {/* Mobile menu button */}
        <button 
          className="md:hidden p-2 rounded-md hover:bg-blue-800"
          onClick={toggleMenu}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
          </svg>
        </button>
      </header>

      <div className="flex flex-col md:flex-row h-[calc(100vh-64px)] overflow-hidden">
        {/* Sidebar - hidden on mobile unless menu is open */}
        <aside className={`
          ${menuOpen || !isMobile ? 'block' : 'hidden'} 
          md:block w-full md:w-1/5 bg-white shadow-md z-10 h-full overflow-y-auto
        `}>
          <nav className="p-4 h-full">
            <h2 className="text-xl font-bold mb-4 text-blue-900 pb-2 border-b border-blue-100">
              Available Demos
            </h2>
            <ul>
              {DEMO_ITEMS.map((demo) => (
                <li key={demo.id} className="mb-2">
                  <button
                    onClick={() => handleDemoSelect(demo)}
                    className={`w-full text-left p-3 rounded-md transition-colors duration-200 
                      ${selectedDemo?.id === demo.id 
                        ? 'bg-blue-100 text-blue-900' 
                        : 'hover:bg-blue-50 text-gray-700'}`}
                  >
                    {demo.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content area - 8/10 width on desktop */}
        <main className="flex-1 md:w-4/5 p-4 md:p-8 h-full overflow-y-auto">
          {selectedDemo && (
            <div className="h-full">
              <h1 className="text-3xl font-bold text-blue-900 mb-2">
                {selectedDemo.id === 'testimonials'
                  ? (language === 'zh' ? '客戶評價' : 'What Our Clients Say')
                  : selectedDemo.title}
              </h1>
              <p className="text-gray-700 mb-6">{selectedDemo.description}</p>
              <div className="h-[calc(100%-120px)]">
                {renderDemoContent()}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Demo; 